import { USER_ROLES } from "@/utils/constants";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { message } from "antd";
import { useUserStore } from "@/stores/userStore";
import { resolveCurrencyCode } from "@/utils/currencyUtils";

/**
 * Maximum file size for images (2MB)
 */
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes

/**
 * Allowed image formats
 */
const ALLOWED_FORMATS = ["image/jpeg", "image/jpg", "image/png"];

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatDate = (dateString: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, options);
};

export const formatCurrency = (amount: number, currencyString?: string | null, fractionDigits?: number): string => {
  let currencyCode: string | null = null;

  if (currencyString) {
    // Handles a bare ISO code ("USD"), "CODE - Name" ("USD - US Dollar"), and a full display name
    // as returned by NetSuite's BUILTIN.DF(t.currency) (e.g. "US Dollar") — a naive first-3-chars
    // substring only works for the first two and silently mis-formats the NetSuite case (e.g.
    // "US Dollar" -> "US ", not a valid currency code).
    currencyCode = resolveCurrencyCode(currencyString);
  } else {
    // Finance users have no single currentCompany (App.tsx nulls it out — they operate across all
    // their assigned companies), so without an explicit currencyString this would otherwise always
    // fall through to the hardcoded INR default below. Fall back to the user's first company, same
    // as the useTheme/UserMenu fallback used elsewhere for this role.
    const state = useUserStore.getState();
    const companyCurrency = state.currentCompany?.currency || state.user?.companies?.[0]?.currency;
    currencyCode = resolveCurrencyCode(companyCurrency);
  }

  // Fall back to INR if nothing resolved to a usable 3-letter code.
  if (!currencyCode) {
    currencyCode = "INR";
  }

  try {
    const options: Intl.NumberFormatOptions = {
      style: "currency",
      currency: currencyCode,
    };
    if (fractionDigits !== undefined) {
      options.minimumFractionDigits = fractionDigits;
      options.maximumFractionDigits = fractionDigits;
    }
    return new Intl.NumberFormat("en-US", options).format(amount);
  } catch (error) {
    console.error(`Invalid currency code: ${currencyCode}`, error);
    // Fallback to INR if the extracted code is still somehow invalid
    const fallbackOptions: Intl.NumberFormatOptions = {
      style: "currency",
      currency: "INR",
    };
    if (fractionDigits !== undefined) {
      fallbackOptions.minimumFractionDigits = fractionDigits;
      fallbackOptions.maximumFractionDigits = fractionDigits;
    }
    return new Intl.NumberFormat("en-US", fallbackOptions).format(amount);
  }
};

/**
 * Gets the display name for a user role
 * @param role - User role key
 * @returns Display name for the role
 */
export const getRoleDisplayName = (role: string): string => {
  return USER_ROLES.find((r) => r.value === role)?.label || "User";
};

/**
 * Determines if a color is light or dark using luminance calculation
 * @param hexColor - Hex color string (e.g., "#ffffff" or "ffffff")
 * @returns 'black' if the color is light, 'white' if the color is dark
 */
export const getContrastTextColor = (hexColor: string): "black" | "white" => {
  // Remove # if present
  const hex = hexColor.replace("#", "");

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance using WCAG formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black text for light backgrounds, white text for dark backgrounds
  return luminance > 0.5 ? "black" : "white";
};

export function generateMidLightColor(): string {
  const min = 90; // avoid dark colors
  const max = 200; // avoid very bright colors

  const randomChannel = () => Math.floor(Math.random() * (max - min + 1)) + min;

  const r = randomChannel();
  const g = randomChannel();
  const b = randomChannel();

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
}

/**
 * Validates file size and format for image uploads
 * @param file - File to validate
 * @param fileType - Type of file for error messages (e.g., "Logo", "Favicon")
 * @returns true if file is valid, false otherwise
 */
export const validateFile = (file: File, fileType: string): boolean => {
  if (file.size > MAX_FILE_SIZE) {
    message.error(`${fileType} size must be less than 2MB`);
    return false;
  }
  if (!ALLOWED_FORMATS.includes(file.type)) {
    message.error("Only JPG, JPEG, and PNG formats are allowed");
    return false;
  }
  return true;
};

// Helper function to check if rating date is current month/year
export const isCurrentMonthYear = (dateString: string): boolean => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const now = new Date();
  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
};

/**
 * Checks if the current browser hostname matches the company domain.
 * Supports direct inclusion (e.g. portal.company.com includes company.com)
 * and subdomain matching (e.g. one.platform.com vs one.com).
 * @param browserHost - Current browser hostname (window.location.hostname)
 * @param companyDomain - Company domain from database
 */
export const checkDomainMatch = (browserHost: string, companyDomain: string): boolean => {
  if (!browserHost || !companyDomain) return false;

  const host = browserHost.toLowerCase();
  const domain = companyDomain.toLowerCase();

  // Direct inclusion check (e.g., portal.apple.com containing apple.com)
  if (host.includes(domain)) return true;

  // Subdomain matching check (e.g., apple.google.com matching apple.com)
  const hostParts = host.split(".");
  const domainParts = domain.split(".");

  // Ensure we have enough parts to compare subdomains
  if (hostParts.length > 0 && domainParts.length > 0) {
    const hostSubdomain = hostParts[0];
    const domainSLD = domainParts[0];
    return hostSubdomain === domainSLD;
  }

  return false;
};
